import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Save } from 'lucide-react'
import { permissionsApi, type PermissionGroup } from '@/api/permissions'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface PermissionMatrixProps {
	departmentId: number
}

export function PermissionMatrix({ departmentId }: PermissionMatrixProps) {
	const [groups, setGroups] = useState<PermissionGroup[]>([])
	const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set())
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// 加载权限分组和部门权限
	useEffect(() => {
		const loadData = async () => {
			try {
				setLoading(true)
				setError(null)
				const [groupsData, deptPerms] = await Promise.all([
					permissionsApi.getGroups(),
					permissionsApi.getDepartmentPermissions(departmentId),
				])
				setGroups(groupsData)
				setSelectedPermissions(new Set(deptPerms.permissions))
			} catch (err) {
				console.error('加载权限数据失败:', err)
				setError('无法加载权限数据')
			} finally {
				setLoading(false)
			}
		}

		if (departmentId) {
			loadData()
		}
	}, [departmentId])

	const togglePermission = (key: string) => {
		setSelectedPermissions(prev => {
			const next = new Set(prev)
			if (next.has(key)) {
				next.delete(key)
			} else {
				next.add(key)
			}
			return next
		})
	}

	const toggleGroup = (groupPerms: string[], checked: boolean) => {
		setSelectedPermissions(prev => {
			const next = new Set(prev)
			if (checked) {
				groupPerms.forEach(p => next.add(p))
			} else {
				groupPerms.forEach(p => next.delete(p))
			}
			return next
		})
	}

	const handleSave = async () => {
		try {
			setSaving(true)
			setError(null)
			await permissionsApi.setDepartmentPermissions(departmentId, {
				permissions: Array.from(selectedPermissions),
			})
			toast.success('权限保存成功')
		} catch (err) {
			console.error('保存权限失败:', err)
			setError('无法保存权限')
		} finally {
			setSaving(false)
		}
	}

	if (loading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-32" />
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-32 w-full" />
			</div>
		)
	}

	if (error) {
		return (
			<Alert variant="destructive">
				<AlertDescription>{error}</AlertDescription>
			</Alert>
		)
	}

	return (
		<div className="space-y-6">
			{error && (
				<Alert variant="destructive">
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			<div className="space-y-4">
				{groups.map(group => {
					const groupPerms = group.permissions.map(p => p.key)
					const allChecked = groupPerms.every(p => selectedPermissions.has(p))
					const someChecked = groupPerms.some(p => selectedPermissions.has(p)) && !allChecked

					return (
						<div key={group.name} className="border rounded-lg p-4">
							<div className="flex items-center gap-2 mb-3">
								<Checkbox
									checked={allChecked}
									className={someChecked ? 'data-[state=unchecked]:border-primary' : ''}
									onCheckedChange={(checked) => toggleGroup(groupPerms, !!checked)}
								/>
								<span className="font-medium">{group.name}</span>
								<span className="text-xs text-muted-foreground">
									({groupPerms.filter(p => selectedPermissions.has(p)).length}/{groupPerms.length})
								</span>
							</div>
							<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ml-6">
								{group.permissions.map(perm => (
									<div key={perm.key} className="flex items-center gap-2">
										<Checkbox
											id={perm.key}
											checked={selectedPermissions.has(perm.key)}
											onCheckedChange={() => togglePermission(perm.key)}
										/>
										<label htmlFor={perm.key} className="text-sm cursor-pointer">
											{perm.label}
										</label>
									</div>
								))}
							</div>
						</div>
					)
				})}
			</div>

			<Button onClick={handleSave} disabled={saving}>
				{saving ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						保存中...
					</>
				) : (
					<>
						<Save className="mr-2 h-4 w-4" />
						保存权限
					</>
				)}
			</Button>
		</div>
	)
}
